import {
  useCallback, useEffect, useMemo, useRef
} from 'react';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import { FEDERATED } from '#client/build-env';
import { useDashboardPath } from '#client/hooks/useDashboardPath';

// Map of services to their custom slot components
const SERVICE_SLOTS = {
  business_license_filing: {
    subscription: <FederatedModule {...FEDERATED.ComplianceUi} exportName="BusinessLicenseFilingCheckoutCallout" />
  }
};

const SlideCheckout = () => {
  const navigate = useNavigate();
  const {
    open,
    openCheckout,
    closeCheckout,
    onClose,
    servicePricingUuids,
    serviceNames,
    servicesToAddToCart
  } = useSlideCheckout();
  const { get, priceListUuid } = usePriceList();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectPathRef = useRef(null);
  const dashboardPath = useDashboardPath();

  const CHECKOUT_COMPLETE_REDIRECT_MAP = useMemo(() => ({
    beneficial_ownership_filing: { path: `${dashboardPath}/f/go/complete_beneficial_ownership_filing` },
    business_kit: { path: dashboardPath, forceReload: false },
    corporate_docs: { path: window.location.pathname, forceReload: true },
    standard_good_standing: { path: dashboardPath, forceReload: false },
    trademarks_search: { path: `${dashboardPath}/compliance/trademarks`, forceReload: true },
    business_license_filing: { path: `${dashboardPath}/compliance/business-licenses/filing-details`, forceReload: true },
    wfg_initial_report: { path: `${dashboardPath}/compliance/initial-reports`, forceReload: false },
    zb_tax_advice: { path: `${dashboardPath}/money/tax-services`, forceReload: false },
    zb_tax_advice_biztax_prep: { path: `${dashboardPath}/money/tax-services`, forceReload: false }
  }), [dashboardPath]);

  const onCloseCheckout = useCallback(({ isPurchaseComplete }) => {
    closeCheckout();
    if (isPurchaseComplete && redirectPathRef.current) {
      // Force reload only for trademarks_search redirect
      if (redirectPathRef.current.forceReload) {
        window.location.href = redirectPathRef.current.path;
      } else {
        navigate(redirectPathRef.current.path);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Listen for the parameter "checkout" in the URL to open the dashboard checkout modal
  // filter must be in the following format: &filter={"interval":"annual","price":200}
  // no filter will default to annual pricing if there is more than one service pricing option
  useEffect(() => {
    const checkoutName = searchParams.get('checkout');
    const filter = searchParams.get('filter');
    if (priceListUuid && checkoutName) {
      const requestedServicePricing = get([checkoutName], filter ? JSON.parse(filter) : { interval: 'annual' });

      const requestedServicePricingUuids = requestedServicePricing
        ?.map(({ pricing }) => pricing?.servicePricingUuid).flat();

      if (requestedServicePricingUuids?.length) {
        // Set the redirect path in the ref before opening checkout
        redirectPathRef.current = CHECKOUT_COMPLETE_REDIRECT_MAP[checkoutName]
          || { path: dashboardPath, forceReload: false };

        openCheckout({
          serviceNames: [checkoutName],
          servicePricingUuids: requestedServicePricingUuids,
          // TODO: remove this once we have the correct interval, it is needed to open the checkout but not actually being used
          interval: 'annual',
          onClose: onCloseCheckout
        });

        // Remove the checkout parameter from the URL
        setSearchParams((params) => {
          params.delete('checkout');
          params.delete('filter');
          return params;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, priceListUuid]);

  const slots = useMemo(() => (
    SERVICE_SLOTS[serviceNames[0]] || SERVICE_SLOTS[servicesToAddToCart[0]?.serviceName]
  ) ?? null, [servicesToAddToCart, serviceNames]);

  return priceListUuid ? (
    <FederatedModule
      {...FEDERATED.EcommerceUi}
      exportName="SlideCheckout"
      isOpen={open}
      onClose={onClose}
      priceListUuid={priceListUuid}
      servicePricingUuids={servicePricingUuids}
      slots={slots}
    />
  ) : null;
};

export default SlideCheckout;
